/**
 * "Nightshift" — original form-rotation level (difficulty 7/10).
 * The Form Foundry curriculum at working speed: tighter hopper fences,
 * denser blinker alternations, pendul arcs, and a mini gauntlet before a
 * pad-launch finale. Authored in block coordinates → standard SSL LevelData.
 */
import type { LevelData, LevelObject } from '../../format/types';
import { UNITS_PER_BLOCK as B } from '../../engine/physics/constants';

let nextId = 1;
const objects: LevelObject[] = [];

function cell(bx: number, by: number): { x: number; y: number } {
  return { x: bx * B + B / 2, y: by * B + B / 2 };
}

function push(o: Omit<LevelObject, 'id'>): void {
  objects.push({ id: nextId++, ...o });
}

const spike = (bx: number, by = 0, hanging = false): void =>
  push({ type: 'spike', ...cell(bx, by), ...(hanging ? { flipY: true } : {}) });
const block = (bx: number, by = 0): void => push({ type: 'block', ...cell(bx, by) });
const ceiling = (bx0: number, bx1: number, by: number): void => {
  for (let bx = bx0; bx <= bx1; bx++) block(bx, by);
};
const portal = (bx: number, kind: string, by = 1): void =>
  push({ type: 'portal', ...cell(bx, by), props: { kind } });
const pad = (bx: number, kind: string): void =>
  push({ type: 'pad', ...cell(bx, 0), props: { kind } });

// --- Runner doubles (0–56) ------------------------------------------------------
spike(24);
spike(25);
spike(32);
spike(33);
spike(41);
spike(48);
spike(49);

// --- Hopper fences, tightened (56–96) -------------------------------------------
portal(56, 'form:hopper');
spike(64);
spike(65);
spike(72);
spike(73);
spike(80);
spike(81);
spike(88);
spike(89);

// --- Blinker alternations, dense (96–136) ----------------------------------------
portal(96, 'form:blinker');
ceiling(98, 134, 5);
spike(104);
spike(105); // ground → blink up
spike(110, 4, true);
spike(111, 4, true); // ceiling → blink down
spike(118);
spike(119);
spike(124, 4, true);
spike(125, 4, true);
spike(130);
spike(131); // ends grounded

// --- Pendul arcs (136–170) --------------------------------------------------------
portal(138, 'form:pendul');
spike(146);
spike(147);
spike(154);
spike(155);
spike(162);
spike(163);
portal(170, 'form:runner', 1);
portal(170, 'form:runner', 4);
portal(171, 'gravity:down', 1);
portal(171, 'gravity:down', 4);

// --- Mini gauntlet (174–202) ------------------------------------------------------
portal(176, 'size:mini');
ceiling(180, 196, 3);
spike(184);
spike(190);
portal(200, 'size:normal');

// --- Pad finale (202–228) ---------------------------------------------------------
pad(208, 'strong');
spike(209);
spike(210);
spike(211);
spike(218);
spike(219);

// Secret shards.
push({ type: 'collectible', ...cell(37, 2), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(114, 2), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(187, 1), props: { kind: 'shard' } });

push({ type: 'endgate', ...cell(228, 2) });

export const NIGHTSHIFT: LevelData = {
  formatVersion: 1,
  meta: {
    name: 'Nightshift',
    author: 'Shape Shifters Team',
    description: 'Every form. No warm-up. The late shift starts now.',
    revision: 1,
    createdAt: '2026-07-24T00:00:00.000Z',
    updatedAt: '2026-07-24T00:00:00.000Z',
    difficulty: 7,
    tags: ['forms', 'official'],
  },
  song: { id: 'undercurrent', bpm: 112, offsetMs: 0 },
  settings: {
    mode: 'normal',
    speed: 'one',
    startForm: 'runner',
    accentColor: '#818cf8',
    hazardColor: '#fb7185',
    backgroundColor: '#0b0a1f',
  },
  objects,
};
